import { useState, useCallback } from 'react';
import { useListFetch, useModal, useRequest } from '../../../../../hooks';
import { notify } from '../../../../../app';
import {
  getKnowledgeSourceChunks,
  searchKnowledge,
  deleteChunks,
} from '../../../../../api';
import type { SearchKnowledgeResponse } from '../../../../../api';
import { useRouteMatch } from 'react-router-dom';

export type FormProps = {
  search: string | null;
  limit?: number;
  threshold?: number;
};

const defaultFormValues: FormProps = {
  search: '',
  limit: 3,
  threshold: 0,
};

export const useKnowledgeSourceDetails = (dataSourceId: string = '') => {
  const [form, setForm] = useState<FormProps>(defaultFormValues);
  const [pageDetails, setPageDetails] = useState(1);
  const [searchResult, setSearchResult] =
    useState<SearchKnowledgeResponse | null>(null);
  const [selectedChunkIds, setSelectedChunkIds] = useState<Set<string>>(
    new Set()
  );
  const {
    isOpened: isDeleteConfirmOpened,
    onOpen: onOpenDeleteConfirm,
    onClose: onCloseDeleteConfirm,
  } = useModal();
  const { params } = useRouteMatch<{ id: string }>();

  const {
    nodes: dataChunks,
    filters,
    setFilters,
    setSort,
    intializing: loadingChunks,
    refresh,
    ...rest
  } = useListFetch(
    async queryParams => {
      if (!dataSourceId) return;

      return await getKnowledgeSourceChunks({
        knowledgeBaseId: params?.id || '',
        dataSourceId,
        page: pageDetails,
        limit: queryParams?.pagination?.limit || 20,
      });
    },
    {
      deps: [params?.id, dataSourceId, pageDetails],
      resultKey: 'chunks',
      withQueryParams: true,
    }
  );

  const [handleSearchKnowledgeRequest, searchingKnowledge] = useRequest(
    _signal => {
      return searchKnowledge({
        query: form.search || '',
        knowledge_base_id: params?.id || '',
        num_results: form.limit || 3,
        threshold: form.threshold || 0,
      });
    },
    {
      onSuccess: data => {
        setSearchResult(data);
      },
      onError: () => {
        notify.error('Failed to search the knowledge');
      },
      deps: [],
    }
  );

  const [handleDeleteChunksRequest, deletingChunks] = useRequest(
    _signal => {
      return deleteChunks({
        knowledgeBaseId: params?.id || '',
        ids: Array.from(selectedChunkIds),
      });
    },
    {
      onSuccess: () => {
        notify.success('Selected chunks deleted successfully');
        setSelectedChunkIds(new Set());
        onCloseDeleteConfirm();
        refresh();
      },
      onError: () => {
        notify.error('Failed to delete selected chunks');
      },
      deps: [selectedChunkIds],
    }
  );

  const handleSelectChunk = useCallback((chunkId: string, checked: boolean) => {
    setSelectedChunkIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(chunkId);
      } else {
        next.delete(chunkId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((allIds: string[], checked: boolean) => {
    if (checked) {
      setSelectedChunkIds(new Set(allIds));
    } else {
      setSelectedChunkIds(new Set());
    }
  }, []);

  const resetSearch = () => {
    setForm(defaultFormValues);
    setSearchResult(null);
  };

  const resetSelection = useCallback(() => {
    setSelectedChunkIds(new Set());
  }, []);

  return {
    handleSearchKnowledge: handleSearchKnowledgeRequest,
    searchingKnowledge,
    searchResult,
    setForm,
    form,
    dataChunks,
    filters,
    setFilters,
    setSort,
    pageDetails,
    setPageDetails,
    loadingChunks,
    refresh,
    resetSearch,
    selectedChunkIds,
    handleSelectChunk,
    handleSelectAll,
    isDeleteConfirmOpened,
    onOpenDeleteConfirm,
    onCloseDeleteConfirm,
    handleDeleteChunks: handleDeleteChunksRequest,
    deletingChunks,
    resetSelection,
    ...rest,
  };
};
